// Must stay the first import: Sentry has to initialise before the store/App
// import graph evaluates, or a module-level throw goes unreported.
import "./instrument";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import { store } from "./app/store";
import App from "./components/App/App";
import AppErrorFallback from "./components/reusableComponents/AppErrorFallback/AppErrorFallback";
import BootMark from "./components/reusableComponents/BootMark/BootMark";
import "./index.css";
import { BrowserRouter } from "react-router-dom";
import { syncSentryUser, showSessionExpiredNoticeIfPending } from "./services/apiRequest";
import { startGpuResetWatchdog } from "./utilities/gpuResetWatchdog";
import { markBootStage, flushPendingBootFailures } from "./utilities/bootWatchdog";

startGpuResetWatchdog();

// Every module in the graph has evaluated: tell the boot sentinel (inlined into
// the HTML by vite.config.js) that the bundle at least parsed and ran, then
// replay any boot failure it recorded on a launch that never got this far.
markBootStage("entry");
flushPendingBootFailures();

// A returning user is auto-logged-in from localStorage and may never touch a
// login screen, so identify the stored session at boot rather than only on
// login. Runs before render so an early crash is attributed too.
syncSentryUser();

// If this load is the tail end of a forced logout, explain it. The web redirect
// is a real document load, so the notice is handed over in sessionStorage.
showSessionExpiredNoticeIfPending();

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={({ error }) => <AppErrorFallback error={error} />}>
      <Provider store={store}>
        {/* v7_startTransition keeps the current page on screen until the next one
            is ready, instead of tearing it down for the <Loader /> fallback in
            App.jsx — React 18 will not show a Suspense fallback during a
            transition. That is only pleasant when the next route arrives quickly:
            it hides the swap, but it also hides the wait, so a slow chunk reads as
            a frozen page with no feedback at all.

            It is on because route chunks are now warmed in the background (see
            utilities/lazyRoute) and no longer wait on per-route stylesheets, so a
            navigation is normally already resolved by the time it is requested.
            If chunk loads ever get slow again, this trade-off inverts — the honest
            fix then is a pending indicator via a data router's useNavigation,
            not just flipping this back off. */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
          <BootMark />
        </BrowserRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
